import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ShoppingBag, Package, ArrowRight, Truck, CheckCircle, Clock,
  Heart, MapPin, User, Tag, Timer, Warning
} from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountHome = () => {
  const { user } = useAuth();
  const { storeSlug } = useParams();
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_orders: 0, total_spent: 0 });
  const [store, setStore] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (storeSlug) {
      axios.get(`${API_URL}/api/s/${storeSlug}`)
        .then(r => setStore(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/s/${storeSlug}/active-offers`)
        .then(r => setOffers(r.data || [])).catch(() => {});
    }
  }, [storeSlug]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/account/orders`, { withCredentials: true });
        setRecentOrders(response.data.slice(0, 3));
        const totalOrders = response.data.length;
        const totalSpent = response.data.reduce((sum, o) => sum + (o.total || 0), 0);
        setStats({ total_orders: totalOrders, total_spent: totalSpent });
      } catch { }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, []);

  const id = store?.identity || {};
  const colors = { primary: id.primary_color || '#c2185b' };
  const openingHours = id.opening_hours || [];
  const closure = id.temporary_closure;
  const isStorePage = !!storeSlug;
  const prefix = isStorePage ? `/store/${storeSlug}` : '';

  const getStatusBadge = (status) => {
    const map = {
      pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
      processing: { label: 'En préparation', cls: 'bg-blue-100 text-blue-700' },
      shipped: { label: 'Expédié', cls: 'bg-purple-100 text-purple-700' },
      delivered: { label: 'Livré', cls: 'bg-emerald-100 text-emerald-700' },
    };
    const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle size={16} className="text-emerald-500" weight="fill" />;
      case 'shipped': return <Truck size={16} className="text-purple-500" weight="fill" />;
      case 'processing': return <Package size={16} className="text-blue-500" weight="fill" />;
      default: return <Clock size={16} className="text-amber-500" weight="fill" />;
    }
  };

  const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long' });

  return (
    <div className="space-y-5" data-testid="account-home">
      {/* Welcome banner */}
      <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primary}dd)` }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative">
          <h1 className="text-2xl font-bold mb-1">Bonjour, {(user?.name || user?.email || '').split(' ')[0]} !</h1>
          <p className="text-white/70 text-[14px]">Bienvenue sur votre espace client {store?.name || ''}</p>
        </div>
      </div>

      {/* Temporary closure alert */}
      {closure?.active && (
        <div className="rounded-xl p-4 border-l-4 bg-amber-50 flex items-start gap-3" style={{ borderColor: colors.primary }} data-testid="closure-alert">
          <Warning size={20} className="text-amber-600 mt-0.5 shrink-0" weight="fill" />
          <div>
            <p className="font-semibold text-amber-800 text-[13px]">{closure.message || 'Fermeture temporaire'}</p>
            {(closure.start_date || closure.end_date) && (
              <p className="text-amber-600 text-[12px] mt-0.5">
                {closure.start_date && `Du ${closure.start_date}`} {closure.end_date && `au ${closure.end_date}`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.primary + '15' }}>
                <ShoppingBag size={20} style={{ color: colors.primary }} weight="fill" />
              </div>
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">Commandes</p>
                <p className="text-2xl font-bold">{stats.total_orders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50">
                <Package size={20} className="text-emerald-600" weight="fill" />
              </div>
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">Total dépensé</p>
                <p className="text-2xl font-bold">{stats.total_spent.toFixed(2)} EUR</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active offers */}
      {offers.length > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="account-offers">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Tag size={16} style={{ color: colors.primary }} weight="fill" />
            <h3 className="text-[14px] font-bold">Offres du moment</h3>
          </div>
          <CardContent className="p-3 space-y-2">
            {offers.slice(0, 3).map(offer => (
              <div key={offer.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                {offer.products?.[0]?.image && (
                  <img src={offer.products[0].image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold truncate">{offer.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[12px] font-bold" style={{ color: colors.primary }}>
                      {offer.discount_type === 'percentage' ? `-${offer.discount_value}%` : offer.discount_type === 'sale_price' ? `${offer.sale_price?.toFixed(2)} EUR` : `-${offer.discount_value} EUR`}
                    </span>
                    {offer.end_date && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                        <Timer size={10} /> Jusqu'au {new Date(offer.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                  </div>
                </div>
                {offer.products?.[0]?.slug && (
                  <Link to={`/store/${storeSlug}/product/${offer.products[0].slug}`}>
                    <Button size="sm" className="text-[11px] h-7 text-white" style={{ backgroundColor: colors.primary }}>Voir</Button>
                  </Link>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recent Orders */}
      <Card className="border-0 shadow-sm" data-testid="account-orders">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[14px] font-bold">Commandes récentes</h3>
          <Link to={`${prefix}/account/orders`} className="text-[12px] font-medium flex items-center gap-1 hover:underline" style={{ color: colors.primary }}>
            Voir tout <ArrowRight size={12} />
          </Link>
        </div>
        <CardContent className="p-3">
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: colors.primary }} />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="py-8 text-center">
              <ShoppingBag size={40} className="mx-auto text-slate-200 mb-3" />
              <p className="text-slate-400 text-[13px] mb-3">Aucune commande pour le moment</p>
              <Link to={`/store/${storeSlug}`}>
                <Button size="sm" className="text-white text-[12px]" style={{ backgroundColor: colors.primary }}>Découvrir la boutique</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentOrders.map(order => (
                <div key={order.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    {getStatusIcon(order.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold">{order.order_number}</p>
                    <p className="text-[11px] text-slate-400">
                      {new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} · {order.items?.length} article(s)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[13px] font-bold">{order.total?.toFixed(2)} EUR</p>
                    {getStatusBadge(order.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Opening hours */}
      {openingHours.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="account-opening-hours">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Clock size={16} style={{ color: colors.primary }} weight="fill" />
            <h3 className="text-[14px] font-bold">Horaires d'ouverture</h3>
          </div>
          <CardContent className="p-4">
            <div className="space-y-1.5">
              {openingHours.map((h, i) => {
                const isToday = today.toLowerCase() === (h.day || dayNames[i] || '').toLowerCase();
                return (
                  <div key={i} className={`flex items-center justify-between py-1.5 px-2 rounded-lg text-[13px] ${isToday ? 'font-semibold' : ''}`}
                    style={isToday ? { backgroundColor: colors.primary + '10', color: colors.primary } : {}}>
                    <span className={isToday ? '' : 'text-slate-600'}>{h.day || dayNames[i]}</span>
                    <span className={h.closed ? 'text-red-400 text-[12px]' : isToday ? '' : 'text-slate-500'}>
                      {h.closed ? 'Fermé' : h.hours || '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3">
        <Link to={`${prefix}/account/orders`}>
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: colors.primary + '12' }}>
                <ShoppingBag size={20} style={{ color: colors.primary }} />
              </div>
              <p className="text-[12px] font-medium text-slate-700">Commandes</p>
            </CardContent>
          </Card>
        </Link>
        <Link to={`${prefix}/account/wishlist`}>
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center bg-pink-50">
                <Heart size={20} className="text-pink-500" />
              </div>
              <p className="text-[12px] font-medium text-slate-700">Favoris</p>
            </CardContent>
          </Card>
        </Link>
        <Link to={`${prefix}/account/profile`}>
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center bg-emerald-50">
                <User size={20} className="text-emerald-600" />
              </div>
              <p className="text-[12px] font-medium text-slate-700">Mon profil</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AccountHome;
